// Booking add-ons (escort vehicles + security personnel).
//
// Prices are set by the assistant during onboarding (see PricingAccount.jsx)
// and arrive from the assistant API. Field keys mirror that form. We search a
// few candidate locations because the detail endpoint may nest pricing under
// `pricing` / `payment` / `pricingAndAccount`.

export const ADDON_UNIT_HOURS = 12;

const ESCORT_VEHICLE_FIELDS = [
  { key: "sedan", label: "Sedan", aliases: ["sedan", "sedanRate"] },
  { key: "suv", label: "SUV", aliases: ["suv", "suvRate"] },
  {
    key: "truck",
    label: "Truck",
    aliases: ["truck", "truckRate", "bus", "busRate"],
  },
];

const SECURITY_PERSONNEL_FIELDS = [
  {
    key: "securityService",
    label: "MOPOL (Mobile Police)",
    aliases: ["securityService", "mopolRate"],
  },
  {
    key: "miceShuttlePrice",
    label: "Police Officer",
    aliases: ["miceShuttlePrice", "policeOfficerRate"],
  },
  {
    key: "pickUpPrice",
    label: "Army Officer",
    aliases: ["pickUpPrice", "armyOfficerRate"],
  },
];

// Fallback options shown when the assistant has no add-on prices configured
// yet (matches the Figma design). Real API prices take precedence over these.
// TODO: Remove once the assistant detail endpoint returns add-on pricing.
const DEFAULT_ESCORT_VEHICLES = [
  { key: "hilux", label: "Hilux", price: 80000, unitHours: ADDON_UNIT_HOURS },
  { key: "prado", label: "Prado", price: 120000, unitHours: ADDON_UNIT_HOURS },
];

const DEFAULT_SECURITY_PERSONNEL = [
  { key: "mopol", label: "MOPOL", price: 40000, unitHours: ADDON_UNIT_HOURS },
  { key: "army", label: "Army", price: 40000, unitHours: ADDON_UNIT_HOURS },
  { key: "police", label: "Police", price: 25000, unitHours: ADDON_UNIT_HOURS },
];

const parsePrice = (value) => {
  if (value == null) return 0;
  const parsed = Number(String(value).replace(/[^\d.]/g, ""));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const collectSources = (source = {}) =>
  [
    source,
    source.pricing,
    source.payment,
    source.pricingAndAccount,
    source.profile?.pricing,
    source.profile?.payment,
  ].filter(Boolean);

const resolvePrice = (sources, aliases) => {
  for (const src of sources) {
    for (const alias of aliases) {
      const price = parsePrice(src?.[alias]);
      if (price > 0) return price;
    }
  }
  return 0;
};

const buildOptions = (source, fields) => {
  const sources = collectSources(source);
  return fields
    .map(({ key, label, aliases }) => ({
      key,
      label,
      price: resolvePrice(sources, aliases),
      unitHours: ADDON_UNIT_HOURS,
    }))
    .filter((option) => option.price > 0);
};

/**
 * Returns { escortVehicles, securityPersonnel } arrays of priced options for
 * the given assistant. Options without a configured price are omitted, so a
 * section simply won't render until the assistant has set those rates.
 */
export const deriveBookingAddOns = (source = {}) => {
  const escortVehicles = buildOptions(source, ESCORT_VEHICLE_FIELDS);
  const securityPersonnel = buildOptions(source, SECURITY_PERSONNEL_FIELDS);
  return {
    escortVehicles:
      escortVehicles.length > 0 ? escortVehicles : DEFAULT_ESCORT_VEHICLES,
    securityPersonnel:
      securityPersonnel.length > 0
        ? securityPersonnel
        : DEFAULT_SECURITY_PERSONNEL,
  };
};

/**
 * Sum of an add-on selection. Each option is priced per ADDON_UNIT_HOURS, so
 * total = price * quantity * (hours / unitHours).
 */
export const computeAddOnsTotal = (selections = []) =>
  selections.reduce((sum, item) => {
    const qty = Number(item?.quantity) || 0;
    const hours = Number(item?.hours) || 0;
    const price = Number(item?.price) || 0;
    const unit = Number(item?.unitHours) || ADDON_UNIT_HOURS;
    if (qty <= 0 || hours <= 0 || price <= 0) return sum;
    return sum + price * qty * (hours / unit);
  }, 0);
